/**
 * HTML Entity Decoder Utility
 * Decodes HTML entities in text to their actual characters
 */

/**
 * Decode HTML entities in a string
 * @param {string} text - Text with HTML entities
 * @returns {string} - Decoded text
 */
export function decodeHTMLEntities(text) {
  if (!text || typeof text !== 'string') {
    return text || '';
  }

  // Common HTML entities map
  const entities = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&apos;': "'",
    '&#x27;': "'",
    '&#x2F;': '/',
    '&#x60;': '`',
    '&#x3D;': '=',
    '&nbsp;': ' ',
    '&ndash;': '–',
    '&mdash;': '—',
    '&copy;': '©',
    '&reg;': '®',
    '&trade;': '™',
    '&ldquo;': '"',
    '&rdquo;': '"',
    '&lsquo;': "'",
    '&rsquo;': "'"
  };

  // Replace known entities
  let decoded = text;
  Object.keys(entities).forEach(entity => {
    const regex = new RegExp(entity, 'g');
    decoded = decoded.replace(regex, entities[entity]);
  });

  // Handle numeric character references (&#123; or &#xABC;)
  decoded = decoded.replace(/&#(\d+);/g, (match, dec) => {
    return String.fromCharCode(dec);
  });

  decoded = decoded.replace(/&#x([0-9A-Fa-f]+);/g, (match, hex) => {
    return String.fromCharCode(parseInt(hex, 16));
  });

  return decoded;
}

/**
 * Decode HTML entities in all string properties of an object
 * @param {Object} obj - Object with potential HTML entities in string properties
 * @returns {Object} - Object with decoded strings
 */
export function decodeObjectHTMLEntities(obj) {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const decoded = {};
  
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    
    if (typeof value === 'string') {
      decoded[key] = decodeHTMLEntities(value);
    } else if (Array.isArray(value)) {
      decoded[key] = value.map(item => 
        typeof item === 'string' ? decodeHTMLEntities(item) : item
      );
    } else {
      decoded[key] = value;
    }
  });

  return decoded;
}

/**
 * Decode HTML entities in specific fields of an equipment item
 * @param {Object} item - Equipment item object
 * @returns {Object} - Equipment item with decoded fields
 */
export function decodeEquipmentItem(item) {
  if (!item) return item;

  return {
    ...item,
    description: decodeHTMLEntities(item.description),
    manufacturer: decodeHTMLEntities(item.manufacturer),
    model: decodeHTMLEntities(item.model),
    notes: decodeHTMLEntities(item.notes),
    name: decodeHTMLEntities(item.name)
  };
}

/**
 * Decode HTML entities in an array of equipment items
 * @param {Array} items - Array of equipment items
 * @returns {Array} - Array with decoded items
 */
export function decodeEquipmentItems(items) {
  if (!Array.isArray(items)) return items;
  
  return items.map(item => decodeEquipmentItem(item));
}